import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { DEFAULT_LIMIT_REQUEST } from '@/Constants';
import { objectNotNullV2 } from '@/Utils/FnUtils';
import { apiClient } from '@/_config/lib/axios/api-client';
import { handleResponseThrowError } from '@/_utils/services';
import type { MotoboyPreRegistration, PreRegistrationsParams } from '../types';
import { GET_PRE_REGISTRATIONS } from './constants';
import type { GetPreRegistrationResponse } from './types';
import { HEADER_TOTAL_COUNT } from '@/_config/lib/axios/interceptors';

const transformParams = (params: PreRegistrationsParams) => {
  const {
    limit,
    page,
    cityId,
    deliverymanName,
    sendPreRegistrationAt,
    preRegistrationFilters,
    vehicleType,
  } = params || {};

  const newPage = page || 1;
  const newLimit = limit || DEFAULT_LIMIT_REQUEST;
  const offset = (newPage - 1) * newLimit;

  return objectNotNullV2({
    limit: newLimit,
    offset,
    cityId,
    deliverymanName,
    sendPreRegistrationAt,
    preRegistrationFilters,
    vehicleType,
  });
};

const getPreRegistrations = (
  params: PreRegistrationsParams,
): Promise<GetPreRegistrationResponse> => {
  const objParams = transformParams(params);

  return apiClient
    .get<MotoboyPreRegistration[]>('/entregadores/pre-registration', {
      params: objParams,
    })
    .then(response => ({
      data: response.data,
      totalCount: parseInt(response.headers[HEADER_TOTAL_COUNT] || '', 10),
    }))
    .catch(handleResponseThrowError());
};

export const useGetPreRegistrations = (
  params: PreRegistrationsParams,
  options: Omit<
    UseQueryOptions<
      GetPreRegistrationResponse,
      unknown,
      GetPreRegistrationResponse,
      (string | PreRegistrationsParams)[]
    >,
    'queryKey' | 'queryFn'
  > = {},
) => {
  return useQuery({
    queryKey: [GET_PRE_REGISTRATIONS, params],
    queryFn: () => getPreRegistrations(params),
    ...options,
  });
};
